"use client";

import clsx from "clsx";
import { useRouter } from "next/navigation";
import { useState, type ReactNode } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { api, useApi } from "@/lib/useApi";
import { Icon } from "../icons";
import { CloseButton } from "../PhonePopup";
import { Button, ErrorBox, Notice } from "../ui";

type Me = { user: { name: string; email: string; role: string } };
type Editing = "name" | "email" | "password" | null;

/** A field: on phones 40 px high with 16 px text (smaller text makes iOS zoom in on focus), compact elsewhere (`wide:`). */
const FIELD = "h-10 w-full min-w-0 rounded-md border border-line bg-surface px-2.5 text-[16px] wide:h-8 wide:text-[12.5px]";

/**
 * The signed-in user's profile (opened from the account menu): a head (initials, name, e-mail, role), then name,
 * e-mail and password as a list — an entry pressed opens its fields below it (one at a time), then its confirmation or
 * the error.
 */
export function ProfileCard({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const router = useRouter();
  const { data, reload } = useApi<Me>("/api/me");
  const [editing, setEditing] = useState<Editing>(null);
  const [status, setStatus] = useState<{ tone: "good" | "bad"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const edit = (k: Editing) => {
    setEditing(k);
    setStatus(null);
  };
  // one change: its request, then its confirmation — the request's own text, else `done` — (or the error); the line closes when it worked
  const save = async (change: () => Promise<string | void>, done: string) => {
    setBusy(true);
    setStatus(null);
    try {
      const text = await change();
      setEditing(null);
      setStatus({ tone: "good", text: text ?? t(done) });
    } catch (err) {
      setStatus({ tone: "bad", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="skeleton h-40 rounded-lg" />;
  const { user } = data;
  const rename = (name: string) =>
    save(async () => {
      await api("/api/me", { method: "PATCH", json: { name } });
      // the name at the top comes with the page
      router.refresh();
      reload();
    }, "profile.renamed");
  // with the platform's mail the new address waits for the link sent to it
  const changeEmail = (email: string, currentPassword: string) =>
    save(async () => {
      const { pending } = await api<{ pending: string | null }>("/api/me/email", { method: "POST", json: { email, currentPassword } });
      reload();
      return pending ? t("profile.emailPending", { email: pending }) : undefined;
    }, "profile.emailChanged");
  const changePassword = (currentPassword: string, newPassword: string) => save(() => api("/api/auth/password", { method: "POST", json: { currentPassword, newPassword } }).then(() => undefined), "profile.passwordChanged");
  const line = { editing, busy, onEdit: edit };

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-[15px] font-semibold">{t("profile.title")}</h2>
        <CloseButton onClose={onClose} className="ml-auto" />
      </div>
      <ProfileHead name={user.name} email={user.email} role={t(user.role === "admin" ? "set.account.roleAdmin" : "set.account.roleUser")} />
      <div className="divide-y divide-line overflow-hidden rounded-xl border border-line text-[13px]">
        <ProfileLine k="name" value={user.name} {...line}>
          <TwoFields first={{ label: t("profile.name"), value: user.name, autoComplete: "name", minLength: 2, maxLength: 40 }} onSave={rename} busy={busy} onCancel={() => edit(null)} />
        </ProfileLine>
        <ProfileLine k="email" value={user.email} {...line}>
          <TwoFields first={{ label: t("profile.newEmail"), value: user.email, type: "email", autoComplete: "email" }} second={{ label: t("profile.currentPassword"), type: "password", autoComplete: "current-password" }} onSave={changeEmail} busy={busy} onCancel={() => edit(null)} />
        </ProfileLine>
        <ProfileLine k="password" value="••••••••••" {...line}>
          <TwoFields first={{ label: t("profile.currentPassword"), type: "password", autoComplete: "current-password" }} second={{ label: t("profile.newPassword"), type: "password", autoComplete: "new-password", minLength: 10, title: t("auth.passwordHint") }} onSave={changePassword} busy={busy} onCancel={() => edit(null)} />
        </ProfileLine>
      </div>
      {status && (status.tone === "bad" ? <ErrorBox>{status.text}</ErrorBox> : <Notice tone="good">✓ {status.text}</Notice>)}
    </section>
  );
}

/** A name's initials: the first letters of its first two words (one word: its first two letters). */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? words[0][0] + words[1][0] : (words[0] ?? "").slice(0, 2);
  return letters.toUpperCase();
}

/** The profile's head: the initials in a circle, the name, the e-mail and the role. */
function ProfileHead({ name, email, role }: { name: string; email: string; role: string }) {
  return (
    <div className="flex items-center gap-3">
      <span aria-hidden className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[16px] font-semibold text-accent-ink">{initialsOf(name)}</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[15px] font-semibold">{name}</p>
        <p className="flex min-w-0 items-center gap-2 text-[12.5px] text-muted">
          <span className="truncate">{email}</span>
          <span className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-[10.5px] font-medium">{role}</span>
        </p>
      </div>
    </div>
  );
}

/**
 * One entry of the profile's list: its label, its value and a chevron — pressed, its fields open below it (the chevron
 * turned down); pressed again, they close.
 */
function ProfileLine({ k, value, editing, busy, onEdit, children }: { k: Exclude<Editing, null>; value: string; editing: Editing; busy: boolean; onEdit: (k: Editing) => void; children: ReactNode }) {
  const { t } = useI18n();
  const open = editing === k;
  return (
    <div>
      <button type="button" aria-expanded={open} onClick={() => onEdit(open ? null : k)} disabled={busy} className="flex min-h-12 w-full cursor-pointer items-center gap-3 px-3 text-left hover:bg-surface-2 disabled:opacity-50">
        <span className="w-20 shrink-0 text-muted">{t(`profile.${k}`)}</span>
        <span className="min-w-0 flex-1 truncate">{value}</span>
        <Icon name="chevronRight" className={clsx("h-4 w-4 shrink-0 text-muted transition-transform", open && "rotate-90")} />
      </button>
      {open && <div className="border-t border-line bg-surface-2/40 p-3">{children}</div>}
    </div>
  );
}

interface FieldSpec {
  label: string;
  value?: string;
  type?: string;
  autoComplete: string;
  minLength?: number;
  maxLength?: number;
  title?: string;
}

/**
 * A change's one or two fields, saved together (the first prefilled where it has a value), each with its label — one
 * below the other on phones, side by side elsewhere — and save and cancel below them (on phones each half the width).
 */
function TwoFields({ first, second, busy, onSave, onCancel }: { first: FieldSpec; second?: FieldSpec; busy: boolean; onSave: (a: string, b: string) => void; onCancel: () => void }) {
  const { t } = useI18n();
  const [a, setA] = useState(first.value ?? "");
  const [b, setB] = useState("");
  const input = (f: FieldSpec, v: string, set: (v: string) => void, autoFocus: boolean) => (
    <label className="block min-w-0 space-y-0.5">
      <span className="block text-[11px] text-ink-2">{f.label}</span>
      <input title={f.title} className={FIELD} type={f.type} autoComplete={f.autoComplete} required minLength={f.minLength} maxLength={f.maxLength} autoFocus={autoFocus} value={v} onChange={(e) => set(e.target.value)} />
    </label>
  );
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(first.type === "password" ? a : a.trim(), b);
      }}
      className="space-y-2"
    >
      <div className={second ? "grid gap-2 wide:grid-cols-2" : "wide:max-w-[320px]"}>
        {input(first, a, setA, true)}
        {second && input(second, b, setB, false)}
      </div>
      <div className="grid grid-cols-2 gap-2 wide:flex wide:justify-end">
        <Button size="sm" type="button" onClick={onCancel} className="phone:h-10">{t("action.cancel")}</Button>
        <Button size="sm" variant="primary" disabled={busy} className="phone:h-10">{busy ? t("set.saving") : t("profile.save")}</Button>
      </div>
    </form>
  );
}
